import { useForm } from '@tanstack/react-form';
import type { UseMutateAsyncFunction } from '@tanstack/react-query';
import { Input, Modal } from '@ui/themed';
import { t } from 'i18next';
import { useHandlers } from '../../../../context/page-context';
import { keyNameFormSchema } from '../../schemas';
import type { CreatedAPIKey } from '../../types';

export function CreateKeyModal({
  createKey,
  isOpen,
  closeModal,
  openShowKeyModal,
}: {
  createKey: UseMutateAsyncFunction<CreatedAPIKey, Error, string, unknown>;
  isOpen: boolean;
  closeModal: () => void;
  openShowKeyModal: () => void;
}) {
  const { notificationHandler } = useHandlers();

  const form = useForm({
    defaultValues: {
      keyName: '',
    },
    validators: {
      onSubmit: keyNameFormSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        await createKey(value.keyName);

        handleClose();
        openShowKeyModal();
      } catch {
        notificationHandler.notify(t('notifications:titles.error'), t('errors:generic'), 'error');
      }
    },
  });

  function handleClose() {
    form.reset();
    closeModal();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      primaryAction={{
        text: t('features:api_settings.components.modals.create_key.submit'),
        onClick: form.handleSubmit,
        loading: form.state.isSubmitting,
      }}
      secondaryAction={{
        text: t('buttons:cancel'),
        onClick: handleClose,
      }}
    >
      <Modal.Header>{t('features:api_settings.components.modals.create_key.header')}</Modal.Header>
      <Modal.Body>
        <form
          onSubmit={(e) => {
            e.stopPropagation();
            e.preventDefault();
            form.setErrorMap({ onSubmit: { fields: {} } });
            form.handleSubmit(e);
          }}
        >
          <form.Field name="keyName">
            {(field) => (
              <Input
                inputMode="text"
                label={t('features:api_settings.components.modals.create_key.body')}
                value={field.state.value}
                onChange={field.setValue}
                error={field.state.meta.errors[0]?.message}
                placeholder={t(
                  'features:api_settings.components.modals.create_key.name_placeholder',
                )}
              />
            )}
          </form.Field>
        </form>
      </Modal.Body>
    </Modal>
  );
}
